<template>
  <div class="page-wrap">
    <!-- ── Premium Table ─────────────────────── -->
    <PremiumTable
      title="Committee Members"
      subtitle="Manage society committee members and their designations"
      add-label="Add"
      :show-edit="true"
      :show-delete="true"
      @add="openAddDialog"
      @edit="openEditDialog"
      @delete="confirmDelete($event.id)"
      :columns="columns"
      :items="committeemembers"
      :loading="loading"
      :total-items="totalRecords"
      :page.sync="page"
      :items-per-page.sync="itemsPerPage"
      :searchable="true"
      :search="search"
      @search="onSearch"
    >
      <!-- Avatar + name column -->
      <template #col-profile_url="{ item }">
        <div class="mem-avatar-wrap">
          <img
            v-if="item.profile_url && item.profile_url !== 'null'"
            :src="API_BASE_URL + '/uploads/' + item.profile_url"
            :alt="item.name"
            class="mem-avatar-img"
          />
          <div
            v-else
            class="mem-avatar-fallback"
            :style="{ background: primaryColor + '20', color: primaryColor }"
          >
            {{
              item.name && item.name !== "null"
                ? item.name.charAt(0).toUpperCase()
                : "U"
            }}
          </div>
          <span class="mem-name">{{
            item.name && item.name !== "null" ? item.name : "—"
          }}</span>
        </div>
      </template>

      <!-- Unit number column -->
      <template #col-house_number="{ item }">
        <div class="house-number-badge">
          <v-icon size="14" color="#94A3B8">mdi-home-outline</v-icon>
          <span v-if="item.flat_name" class="flat-prefix"
            >{{ item.flat_name }}-</span
          >
          <span class="house-num">{{ item.house_number }}</span>
        </div>
      </template>

      <template #col-mobile_no="{ item }">
        <div class="mobile-cell">
          <v-icon size="14" color="#94A3B8" class="mr-1"
            >mdi-phone-outline</v-icon
          >
          <span
            >{{ item.country_code ? item.country_code : "" }}
            {{ item.mobile_no }}</span
          >
        </div>
      </template>

      <!-- Role badge column -->
      <template #col-role="{ item }">
        <span class="role-tag">{{ item.role || "—" }}</span>
      </template>

      <!-- Business column -->
      <template #col-business="{ item }">
        <span class="date-text">{{ item.business || "—" }}</span>
      </template>
    </PremiumTable>

    <!-- ── Add / Edit Modal ─────────────────── -->
    <PremiumModal
      v-model="addCommitteeDialog"
      :title="(committee.id ? 'Edit' : 'Add') + ' Committee Member'"
      :loading="saveLoading"
      submit-text="Submit"
      max-width="480"
      @submit="
        committee.id
          ? updateCommitteeMember(committee.id)
          : saveCommitteeMember()
      "
      @close="closeDialog"
    >
      <v-row dense>
        <!-- Block selector (multi-building only) -->
        <v-col v-if="useBlocks" cols="12">
          <PremiumSelect
            v-model="committee.flat_id"
            label="Block"
            :items="flats"
            item-text="name"
            item-value="id"
            placeholder="Select block"
            icon="mdi-office-building-outline"
            v-validate="!!committee.id ? '' : 'required'"
            name="flat"
            data-vv-as="Block"
            :error="errors.first('flat') || serverSideErrors.flat_id"
            @change="getFlatMember"
            :disabled="!!committee.id"
            :readonly="!!committee.id"
          />
        </v-col>

        <!-- Member selector -->
        <v-col cols="12">
          <PremiumSelect
            v-model="committee.member_id"
            label="Member"
            :items="members"
            item-text="display_name"
            item-value="id"
            placeholder="Select member"
            icon="mdi-account-outline"
            v-validate="!!committee.id ? '' : 'required'"
            name="member_id"
            data-vv-as="Member"
            :error="errors.first('member_id') || serverSideErrors.member_id"
            :disabled="!!committee.id"
            :readonly="!!committee.id"
          />
        </v-col>

        <!-- Designation -->
        <v-col cols="12">
          <PremiumInput
            v-model="committee.role"
            label="Designation"
            placeholder="e.g. President, Secretary"
            icon="mdi-briefcase-outline"
            v-validate="'required'"
            name="role"
            data-vv-as="Designation"
            :error="errors.first('role') || serverSideErrors.role"
          />
        </v-col>
      </v-row>
    </PremiumModal>

    <!-- ── Confirm Delete Modal ─────────────── -->
    <ConfirmModal
      v-model="deleteDialog"
      type="danger"
      title="Remove Committee Member"
      message="Are you sure you want to remove this committee member? They will lose their committee role."
      confirm-text="Yes, Remove"
      cancel-text="Cancel"
      :loading="deleteLoading"
      @confirm="deleteCommitteemember"
    />
  </div>
</template>

<script>
import { mapMutations, mapGetters, mapState } from "vuex";
import PremiumTable from "../../common/PremiumTable.vue";
import PremiumModal from "../../common/PremiumModal.vue";
import PremiumInput from "../../common/PremiumInput.vue";
import PremiumSelect from "../../common/PremiumSelect.vue";
import ConfirmModal from "../../common/ConfirmModal.vue";
import filterMixin from "../../../mixins/filterMixin";
import { mapMembersWithDisplayName } from "@/helpers/memberDisplay";

export default {
  name: "CommitteeMember",
  mixins: [filterMixin],
  components: {
    PremiumTable,
    PremiumModal,
    PremiumInput,
    PremiumSelect,
    ConfirmModal,
  },

  data() {
    return {
      committeemembers: [],
      members: [],
      flats: [],
      totalRecords: 0,
      loading: false,
      saveLoading: false,
      deleteLoading: false,
      deleteDialog: false,
      addCommitteeDialog: false,
      delete_id: null,
      selected_type: "flat",
      serverSideErrors: {},
      page: 1,
      itemsPerPage: 10,
      search: "",
      user: {},
      committee: {
        id: null,
        member_id: "",
        flat_id: "",
        role: "",
      },
    };
  },

  computed: {
    ...mapGetters("society", ["useBlocks", "unitLabel", "unitType"]),
    ...mapState("theme", ["primaryColor"]),
    API_BASE_URL: () => process.env.VUE_APP_API_URL,

    societyUnitType() {
      return this.unitType;
    },

    columns() {
      return [
        { key: "profile_url", label: "Member", width: "260px" },
        {
          key: "house_number",
          label: this.unitLabel || "Unit",
          width: "120px",
        },
        {
          key: "mobile_no",
          label: this.$t("committee.headers.contact_no") || "Contact",
          width: "180px",
        },
        {
          key: "role",
          label: this.$t("committee.headers.designation") || "Role",
          width: "180px",
        },
        {
          key: "business",
          label: this.$t("committee.headers.business") || "Business",
          minWidth: "160px",
        },
      ];
    },
  },

  watch: {
    useBlocks(newVal) {
      if (!newVal) this.committee.flat_id = "";
    },
    page() {
      this.getCommitteeMembers();
    },
    itemsPerPage() {
      this.page = 1;
      this.getCommitteeMembers();
    },
    search() {
      this.page = 1;
      this.getCommitteeMembers();
    },
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),

    onSearch(val) {
      this.search = val;
    },

    getCommitteeMembers() {
      this.loading = true;
      this.$axios
        .get("societyadmin/committeeMembers", {
          params: {
            page: this.page,
            perPage: this.itemsPerPage,
            sortBy: "created_at",
            sortDesc: true,
            search: this.search,
          },
        })
        .then((response) => {
          this.loading = false;
          const data = response.data;
          if (data.status) {
            this.committeemembers = data.committeeMembers;
            this.totalRecords = data.total;
            if (data.page > data.lastPage) this.page = 1;
          } else {
            this.showError(data.message);
            if (data.type === "invalid") {
              localStorage.removeItem("user");
              this.$router.push("/societyadmin/login");
            }
          }
        })
        .catch(() => {
          this.loading = false;
        });
    },

    getFlat() {
      this.$axios.get("societyadmin/flats").then((response) => {
        const data = response.data;
        if (data.status) {
          let flats = [...data.flats];
          if (this.unitType === 3 && this.useBlocks) {
            flats.unshift({ id: "all_shops", name: "All Shops" });
          }
          this.flats = flats;
        } else this.showError(data.message);
      });
    },

    getFlatMember() {
      let url = "/societyadmin/members?society_id=" + this.user.id;
      if (this.committee.flat_id === "all_shops") {
        url += "&unit_type=2";
      } else if (
        this.committee.flat_id &&
        this.committee.flat_id !== "null" &&
        this.committee.flat_id !== ""
      ) {
        url = "/societyadmin/flats/" + this.committee.flat_id + "/members";
      }
      this.$axios.get(url).then((response) => {
        const data = response.data;
        if (data.status) {
          const arr = Array.isArray(data.members)
            ? data.members
            : data.members.data || [];
          this.members = mapMembersWithDisplayName(arr, this.societyUnitType);
        } else {
          this.showError(data.message);
        }
      });
    },

    openAddDialog() {
      this.committee = { id: null, member_id: "", flat_id: "", role: "" };
      this.$validator.reset();
      this.serverSideErrors = {};
      if (!this.useBlocks) this.getFlatMember();
      this.addCommitteeDialog = true;
    },

    openEditDialog(item) {
      this.committee = {
        id: item.id,
        member_id: item.member_id,
        flat_id: item.flat_id,
        role: item.role,
      };
      this.$validator.reset();
      this.serverSideErrors = {};
      this.getFlatMember();
      this.addCommitteeDialog = true;
    },

    saveCommitteeMember() {
      this.$validator.validateAll().then((success) => {
        if (!success) return;
        this.saveLoading = true;
        this.$axios
          .post("/societyadmin/committeeMembers", this.committee)
          .then((response) => {
            this.saveLoading = false;
            const data = response.data;
            if (data.status) {
              this.getCommitteeMembers();
              this.closeDialog();
            } else {
              this.handleErrors(data);
            }
          })
          .catch(() => {
            this.saveLoading = false;
          });
      });
    },

    updateCommitteeMember(id) {
      this.$validator.validateAll().then((success) => {
        if (!success) return;
        this.saveLoading = true;
        this.committee.society_id = this.user.id;
        this.$axios
          .put("/societyadmin/committeeMembers/" + id, this.committee)
          .then((response) => {
            this.saveLoading = false;
            const data = response.data;
            if (data.status) {
              this.getCommitteeMembers();
              this.closeDialog();
            } else {
              this.handleErrors(data);
            }
          })
          .catch(() => {
            this.saveLoading = false;
          });
      });
    },

    confirmDelete(id) {
      this.delete_id = id;
      this.deleteDialog = true;
    },

    deleteCommitteemember() {
      this.deleteLoading = true;
      this.$axios
        .delete("societyadmin/committeeMembers/" + this.delete_id)
        .then((response) => {
          this.deleteLoading = false;
          const data = response.data;
          if (data.status) {
            this.deleteDialog = false;
            this.getCommitteeMembers();
            this.setAlert({
              showAlert: true,
              variant: "green accent-4",
              timeout: "2000",
              message: data.message,
            });
          } else {
            this.showError(data.message);
          }
        })
        .catch(() => {
          this.deleteLoading = false;
        });
    },

    closeDialog() {
      this.deleteDialog = false;
      this.addCommitteeDialog = false;
      this.committee = { id: null, member_id: "", flat_id: "", role: "" };
      this.serverSideErrors = {};
      this.$validator.reset();
    },

    handleErrors(data) {
      if (data.errors) {
        this.serverSideErrors = {};
        data.errors.forEach((e) => {
          this.serverSideErrors[e.field] = e.message;
        });
      } else {
        this.showError(data.message);
      }
    },

    showError(msg) {
      this.setAlert({
        showAlert: true,
        variant: "red accent-3",
        timeout: "2000",
        message: msg,
      });
    },
  },

  mounted() {
    this.user = localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : {};

    // Initialize persistent filters
    this.initFilters("committemember");

    this.getFlat();
    this.committee.flat_id = "";
    if (!this.useBlocks) {
      this.selected_type = "single_house";
      this.getFlatMember();
    }
    this.getCommitteeMembers();
    if (this.$route.query.add === "1") {
      this.$nextTick(() => this.openAddDialog());
    }
  },
};
</script>

<style scoped>
.page-wrap {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* ── Avatar ── */
.mem-avatar-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
}
.mem-avatar-img {
  width: 34px;
  height: 34px;
  min-width: 34px;
  min-height: 34px;
  border-radius: 50%;
  object-fit: contain;
  flex-shrink: 0;
  border: 2px solid var(--border, #e2e8f0);
}
.mem-avatar-fallback {
  width: 34px;
  height: 34px;
  min-width: 34px;
  min-height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}
.mem-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--tx, #111827);
}

/* ── Cells ── */
.house-number-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px;
  border-radius: 8px;
  background: var(--surface2, #f8fafc);
  border: 1px solid var(--border, #e2e8f0);
  font-size: 12px;
  /* font-weight: 700; */
  color: var(--tx, #111827);
}
.house-number-badge .flat-prefix {
  color: var(--tx3, #6b7280);
}
.house-number-badge .house-num {
  color: var(--tx3, #6b7280);
}
.unit-tag {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  background: var(--surface2, #f8fafc);
  border: 1px solid var(--border, #e2e8f0);
  font-size: 11px;
  font-weight: 600;
  color: var(--tx2, #374151);
}
.role-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px 3px 7px;
  border-radius: 20px;
  background: rgba(13, 153, 114, 0.1);
  color: #0d9972;
  font-size: 11px;
  font-weight: 700;
}
.role-tag::before {
  content: "";
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #0d9972;
  flex-shrink: 0;
}
.date-text {
  font-size: 12.5px;
  color: var(--tx2, #374151);
}
</style>
